if (!customElements.get('studies-tabs')) {
  class StudiesTabs extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.sectionId;
      this.activeIndex = Number(this.dataset.activeIndex) || 1;
      this.tabElements = Array.from(this.querySelectorAll('[data-studies-tab]'));
      this.panelElement = this.querySelector('[data-studies-panel]');

      this.cachedPanelContent = new Map();
      this.abortController = null;

      if (this.panelElement) {
        this.cachedPanelContent.set(this.activeIndex, this.panelElement.innerHTML);
      }

      this.onTabClick = this.onTabClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.tabElements.forEach((tabElement) => tabElement.addEventListener('click', this.onTabClick));

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      this.tabElements.forEach((tabElement) => tabElement.removeEventListener('click', this.onTabClick));
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.abortController?.abort();
    }

    onTabClick(event) {
      const tabElement = event.currentTarget;

      this.selectTab(Number(tabElement.dataset.index));
    }

    onBlockSelect(event) {
      const tabElement = event.target.closest('[data-studies-tab]');

      if (!tabElement || !this.contains(tabElement)) return;

      this.selectTab(Number(tabElement.dataset.index));
    }

    async selectTab(index) {
      if (!index || index === this.activeIndex) return;

      this.activeIndex = index;
      this.updateTabs();

      this.abortController?.abort();
      this.abortController = null;

      if (this.cachedPanelContent.has(index)) {
        this.renderPanel(index);
        return;
      }

      const abortController = new AbortController();
      this.abortController = abortController;
      this.panelElement.setAttribute('data-loading', '');

      try {
        const panelContent = await this.fetchPanelContent(index, abortController.signal);

        this.cachedPanelContent.set(index, panelContent);

        if (index === this.activeIndex) {
          this.renderPanel(index);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error(error);
      } finally {
        if (this.abortController === abortController) {
          this.abortController = null;
          this.panelElement.removeAttribute('data-loading');
        }
      }
    }

    async fetchPanelContent(index, signal) {
      const requestUrl = new URL(window.location.href);

      requestUrl.searchParams.set('section_id', this.sectionId);
      requestUrl.searchParams.set('page', index);

      const response = await fetch(requestUrl.toString(), { signal });

      if (!response.ok) {
        throw new Error(`Studies tabs: failed to load tab ${index} (${response.status})`);
      }

      const responseText = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const responsePanelElement = responseDocument.querySelector('[data-studies-panel]');

      if (!responsePanelElement) {
        throw new Error(`Studies tabs: panel is missing in response for tab ${index}`);
      }

      return responsePanelElement.innerHTML;
    }

    updateTabs() {
      this.tabElements.forEach((tabElement) => {
        const isActive = Number(tabElement.dataset.index) === this.activeIndex;

        tabElement.disabled = isActive;
        tabElement.setAttribute('aria-selected', isActive);

        if (isActive) {
          this.panelElement.setAttribute('aria-labelledby', tabElement.id);
        }
      });
    }

    renderPanel(index) {
      this.panelElement.innerHTML = this.cachedPanelContent.get(index);
      this.panelElement.removeAttribute('data-loading');
    }
  }

  customElements.define('studies-tabs', StudiesTabs);
}
